import { ResponsiveBar } from "@nivo/bar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { EmptyState } from "@/components/shared/empty-state";
import { getQuarterLabel } from "@/lib/status-machine";
import type { QuarterCompletion } from "@/hooks/use-dashboard";

interface QuarterCompletionChartProps {
  data: QuarterCompletion[];
  year?: number;
}

// Hillsdale palette: Navy, Blue, Gold, Red mapped to completion ranges
function getBarColor(value: number): string {
  if (value >= 75) return "hsl(40, 63%, 48%)";     // Gold — on track
  if (value >= 50) return "hsl(213, 52%, 63%)";    // Bright Blue — progressing
  if (value >= 25) return "hsl(228, 65%, 19%)";    // Navy — early stages
  return "hsl(4, 64%, 46%)";                        // Red — behind
}

export function QuarterCompletionChart({ data, year }: QuarterCompletionChartProps) {
  if (data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Completion by Quarter</CardTitle>
        </CardHeader>
        <CardContent>
          <EmptyState
            title="No quarterly data"
            description="Key results will appear here once created."
          />
        </CardContent>
      </Card>
    );
  }

  const chartData = data.map((q) => ({
    quarter: getQuarterLabel(q.quarter).split(" ")[0] ?? q.quarter,
    fullLabel: getQuarterLabel(q.quarter, year),
    completion: Math.round(q.completion_percent),
    totalKrs: q.total_key_results,
    color: getBarColor(q.completion_percent),
  }));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Completion by Quarter</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-64">
          <ResponsiveBar
            data={chartData}
            keys={["completion"]}
            indexBy="quarter"
            margin={{ top: 10, right: 20, bottom: 40, left: 50 }}
            padding={0.4}
            valueScale={{ type: "linear", min: 0, max: 100 }}
            colors={({ data: d }) => d.color as string}
            borderRadius={4}
            axisLeft={{
              tickSize: 0,
              tickPadding: 8,
              format: (v) => `${v}%`,
            }}
            axisBottom={{
              tickSize: 0,
              tickPadding: 8,
            }}
            enableGridY={true}
            gridYValues={[0, 25, 50, 75, 100]}
            enableLabel={true}
            label={(d) => `${d.value}%`}
            labelSkipWidth={24}
            labelTextColor="hsl(0, 0%, 100%)"
            tooltip={({ data: d }) => (
              <div className="rounded-md bg-[hsl(var(--popover))] px-3 py-2 text-sm shadow-md border">
                <p className="font-semibold">{d.fullLabel}</p>
                <p>{d.completion}% complete</p>
                <p className="text-[hsl(var(--muted-foreground))]">{d.totalKrs} key results</p>
              </div>
            )}
            theme={{
              text: { fontFamily: "Source Sans 3, sans-serif", fill: "hsl(228, 65%, 19%)" },
              grid: { line: { stroke: "hsl(200, 18%, 85%)", strokeWidth: 1 } },
              axis: { ticks: { text: { fill: "hsl(228, 20%, 46%)" } } },
            }}
            role="img"
            ariaLabel="Quarterly completion chart"
          />
        </div>
      </CardContent>
    </Card>
  );
}
